// 🕐 Heure de Paris pour le planning, quel que soit le fuseau du navigateur : les créneaux
// arrivent de l'API en UTC et s'affichent toujours en heure de Paris (jamais avec moment()
// seul, qui prend le fuseau du navigateur). Dates de Paris « AAAA-MM-JJ », heures « HH:mm ».
export const TIME_ZONE = "Europe/Paris";

const pad = (n) => String(n).padStart(2, "0");
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const localeOf = (language) => (language === "en" ? "en-GB" : "fr-FR");

const partsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23", weekday: "short",
});

// Date, heure et jour de la semaine de Paris d'un instant (1 = lundi … 7 = dimanche)
export const partsOf = (instant) => {
  const parts = partsFormatter.formatToParts(new Date(instant));
  const get = (type) => parts.find((p) => p.type === type).value;
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${get("hour")}:${get("minute")}`, weekday: WEEKDAYS.indexOf(get("weekday")) + 1 };
};

export const todayOf = (now = new Date()) => partsOf(now).date;

// Calendrier, sans fuseau
export const addDays = (date, n) => {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
export const weekdayOf = (date) => {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
};

// « HH:mm » ↔ minutes depuis minuit (« 24:00 » = 1440)
export const minutesOf = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};
export const timeOf = (minutes) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

// Heure de Paris d'un instant : « 14:00 »
export const formatTime = (instant) => partsOf(instant).time;

// Jour d'une date de Paris : « lundi 19 octobre » (fr) / « Monday 19 October » (en). Assemblé à
// partir des morceaux (jour, numéro, mois) : même libellé quelle que soit la version d'ICU du
// navigateur ou de Node (ICU 72, celui de Node 18.15, écrivait « Monday, 19 October »)
export const formatDay = (date, language = "fr") => {
  const parts = new Intl.DateTimeFormat(localeOf(language), { timeZone: "UTC", weekday: "long", day: "numeric", month: "long" }).formatToParts(new Date(`${date}T12:00:00Z`));
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get("weekday")} ${get("day")} ${get("month")}`;
};

// Date de Paris d'un instant, en toutes lettres : « 2 novembre 2026 », « 1er novembre 2026 » (fr, espaces
// insécables) / « 2 November 2026 » (en). Vide si l'instant manque (tour 67, dates des avis)
export const formatLongDate = (instant, language = "fr") => {
  if (instant === null || instant === undefined || Number.isNaN(new Date(instant).getTime())) return "";
  const parts = new Intl.DateTimeFormat(localeOf(language), { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" }).formatToParts(new Date(`${partsOf(instant).date}T12:00:00Z`));
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const day = get("day") === "1" && language !== "en" ? "1er" : get("day");
  return [day, get("month"), get("year")].join(" ");
};

// Jour et heure de Paris d'un instant : « lundi 19 octobre à 14:00 » / « Monday 19 October at 14:00 »
export const formatDateTime = (instant, language = "fr") => {
  const { date, time } = partsOf(instant);
  return `${formatDay(date, language)} ${language === "en" ? "at" : "à"} ${time}`;
};

// Nom du jour de la semaine (1 = lundi … 7 = dimanche) : « lundi » / « Monday »
export const weekdayName = (weekday, language = "fr") =>
  new Intl.DateTimeFormat(localeOf(language), { timeZone: "UTC", weekday: "long" }).format(new Date(Date.UTC(2026, 9, 18 + weekday, 12)));

// 📅 Tour 65 (D) : ce que faisait moment, retiré du site, en heure de Paris
const validOf = (instant) => instant !== null && instant !== undefined && !Number.isNaN(new Date(instant).getTime());

// Date courte de Paris d'un instant : « 03/11/2026 » (vide si l'instant manque)
export const formatDate = (instant) => {
  if (!validOf(instant)) return "";
  const [y, m, d] = partsOf(instant).date.split("-");
  return `${d}/${m}/${y}`;
};

// Jours de calendrier entre deux dates de Paris (b − a)
export const daysBetween = (a, b) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000);

// Jour de l'instant passé ou du jour (date de vérification d'un e-mail ou d'un téléphone)
export const isDayReached = (instant, now = new Date()) => validOf(instant) && partsOf(instant).date <= todayOf(now);

// Mêmes textes que le calendar() de moment (fr et en), en heure de Paris : « Aujourd’hui à 14:30 », « Hier à 09:05 »,
// « lundi dernier à 18:00 », « Demain à 10:00 », « jeudi à 10:00 », sinon « 03/11/2026 ». Anglais : « Today at 2:30 PM »,
// « Last Monday at 6:00 PM », « 11/03/2026 »
export const calendarOf = (instant, language = "fr", now = new Date()) => {
  if (!validOf(instant)) return "";
  const { date, time, weekday } = partsOf(instant);
  const days = daysBetween(todayOf(now), date);
  const [y, m, d] = date.split("-");
  if (language === "en") {
    const [h, min] = time.split(":").map(Number);
    const at = `at ${h % 12 || 12}:${pad(min)} ${h < 12 ? "AM" : "PM"}`;
    const day = weekdayName(weekday, "en");
    if (days <= -7 || days >= 7) return `${m}/${d}/${y}`;
    if (days <= -2) return `Last ${day} ${at}`;
    return days === -1 ? `Yesterday ${at}` : days === 0 ? `Today ${at}` : days === 1 ? `Tomorrow ${at}` : `${day} ${at}`;
  }
  const day = weekdayName(weekday, "fr");
  if (days <= -7 || days >= 7) return `${d}/${m}/${y}`;
  if (days <= -2) return `${day} dernier à ${time}`;
  return days === -1 ? `Hier à ${time}` : days === 0 ? `Aujourd’hui à ${time}` : days === 1 ? `Demain à ${time}` : `${day} à ${time}`;
};
